import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import { BrutalInput } from "@/components/ui/BrutalInput";
import KPICard from "@/components/admin/KPICard";
import {
  BrutalSelect,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectTrigger,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Plus, Bell, Trash2, Pencil, Power, Link2, Search, Mail, MailX, Zap, ArrowLeft } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { it, enUS, es } from "date-fns/locale";
import NotificationFormModal, { AdminNotification, AudienceTarget } from "@/components/admin/NotificationFormModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function AdminNotifications() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState<AdminNotification | null>(null);
  const [deleteNotificationId, setDeleteNotificationId] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const localeMap = {
    it: it,
    en: enUS,
    es: es,
  };

  // Fetch notifications
  const { data: notifications, isLoading } = useQuery({
    queryKey: ["admin-notifications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_notifications")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as AdminNotification[];
    },
  });

  // Toggle active status
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from("admin_notifications")
        .update({ is_active: isActive })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
      toast.success(t("admin.notifications.statusUpdated", "Stato aggiornato"));
    },
    onError: (error) => {
      console.error("Error updating notification:", error);
      toast.error(t("common.error"));
    },
  });

  // Delete notification
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("admin_notifications")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
      toast.success(t("admin.notifications.deleted", "Notifica eliminata"));
      setDeleteNotificationId(null);
    },
    onError: (error) => {
      console.error("Error deleting notification:", error);
      toast.error(t("common.error"));
    },
  });

  const getTitle = (notification: AdminNotification) => {
    const langKey = `title_${i18n.language}` as keyof AdminNotification;
    return (notification[langKey] as string) || notification.title_it;
  };

  const getAudienceLabel = (target: AudienceTarget): string => {
    const labels: Record<AudienceTarget, string> = {
      all: t("admin.notifications.audienceAll", "Tutti"),
      free: t("admin.notifications.audienceFree", "Free"),
      base: "Base",
      pro: "Pro",
      plus: "Plus",
    };
    return labels[target] || target;
  };

  const getAudienceBadges = (notification: AdminNotification) => {
    const audience = (notification.target_audience as AudienceTarget[]) || ["all"];
    return audience;
  };

  // Filter notifications
  const filteredNotifications = notifications?.filter((notification) => {
    // Search filter
    const title = getTitle(notification).toLowerCase();
    if (searchTerm && !title.includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Audience filter
    if (audienceFilter !== "all") {
      const targets = (notification.target_audience as AudienceTarget[]) || ["all"];
      if (!targets.includes(audienceFilter as AudienceTarget)) {
        return false;
      }
    }

    // Status filter
    if (statusFilter === "active" && !notification.is_active) {
      return false;
    }
    if (statusFilter === "inactive" && notification.is_active) {
      return false;
    }

    return true;
  }) || [];

  // Calculate KPIs
  const totalCount = notifications?.length || 0;
  const activeCount = notifications?.filter(n => n.is_active).length || 0;
  const evergreenCount = notifications?.filter(n => n.is_evergreen).length || 0;
  const withCtaCount = notifications?.filter(n => n.cta_url).length || 0;

  const handleOpenCreate = () => {
    setEditingNotification(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (notification: AdminNotification) => {
    setEditingNotification(notification);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingNotification(null);
  };

  const audienceOptions = [
    { value: "all", label: "Tutti gli audience" },
    { value: "free", label: "Free" },
    { value: "base", label: "Base" },
    { value: "pro", label: "Pro" },
    { value: "plus", label: "Plus" },
  ];

  const statusOptions = [
    { value: "all", label: "Tutti gli stati" },
    { value: "active", label: "Attive" },
    { value: "inactive", label: "Inattive" },
  ];

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.notifications.title", "Gestione Notifiche")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="p-2 hover:bg-accent transition-colors rounded-lg"
              aria-label={t("common.back")}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
                <Bell className="w-10 h-10 text-yellow-600" />
                {t("admin.notifications.title", "Gestione Notifiche")}
              </h1>
              <p className="text-muted-foreground mt-1">
                {t("admin.notifications.subtitle", "Crea e gestisci notifiche broadcast per tutti gli utenti")}
              </p>
            </div>
          </div>
          <BrutalButton onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-2" />
            {t("admin.notifications.createNew", "Crea Notifica")}
          </BrutalButton>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard
            title="Totale Notifiche"
            value={totalCount}
          />
          <KPICard
            title="Attive"
            value={activeCount}
          />
          <KPICard
            title="Evergreen"
            value={evergreenCount}
          />
          <KPICard
            title="Con CTA"
            value={withCtaCount}
          />
        </div>

        {/* Filters */}
        <BrutalCard className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <BrutalInput
                  placeholder="Cerca per titolo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-full md:w-48">
              <BrutalSelect value={audienceFilter} onValueChange={setAudienceFilter}>
                <BrutalSelectTrigger>
                  <BrutalSelectValue placeholder="Audience" />
                </BrutalSelectTrigger>
                <BrutalSelectContent>
                  {audienceOptions.map((option) => (
                    <BrutalSelectItem key={option.value} value={option.value}>
                      {option.label}
                    </BrutalSelectItem>
                  ))}
                </BrutalSelectContent>
              </BrutalSelect>
            </div>
            <div className="w-full md:w-40">
              <BrutalSelect value={statusFilter} onValueChange={setStatusFilter}>
                <BrutalSelectTrigger>
                  <BrutalSelectValue placeholder="Stato" />
                </BrutalSelectTrigger>
                <BrutalSelectContent>
                  {statusOptions.map((option) => (
                    <BrutalSelectItem key={option.value} value={option.value}>
                      {option.label}
                    </BrutalSelectItem>
                  ))}
                </BrutalSelectContent>
              </BrutalSelect>
            </div>
          </div>
        </BrutalCard>

        {/* Table */}
        {isLoading ? (
          <BrutalCard className="p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </BrutalCard>
        ) : filteredNotifications.length === 0 ? (
          <BrutalCard className="p-8 text-center">
            <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {t("admin.notifications.noNotifications", "Nessuna notifica trovata")}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || audienceFilter !== "all" || statusFilter !== "all"
                ? "Prova a modificare i filtri di ricerca"
                : "Crea la tua prima notifica broadcast"}
            </p>
            {!searchTerm && audienceFilter === "all" && statusFilter === "all" && (
              <BrutalButton onClick={handleOpenCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Crea Notifica
              </BrutalButton>
            )}
          </BrutalCard>
        ) : (
          <BrutalCard>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface border-b-[3px] border-dark">
                  <tr>
                    <th className="px-4 py-3 text-left font-heading font-bold min-w-[250px]">Titolo</th>
                    <th className="px-4 py-3 text-left font-heading font-bold">Audience</th>
                    <th className="px-4 py-3 text-left font-heading font-bold">CTA</th>
                    <th className="px-4 py-3 text-center font-heading font-bold">Email</th>
                    <th className="px-4 py-3 text-left font-heading font-bold">Data</th>
                    <th className="px-4 py-3 text-right font-heading font-bold">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotifications.map((notification) => (
                    <tr key={notification.id} className="border-b border-border hover:bg-surface/50 transition-colors">
                      {/* Title with image and badges */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 border-2 border-dark overflow-hidden bg-muted flex-shrink-0">
                            {notification.image_url ? (
                              <img
                                src={notification.image_url}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Bell className="h-4 w-4 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium truncate">
                              {getTitle(notification)}
                            </span>
                            <BrutalBadge variant={notification.is_active ? "default" : "secondary"}>
                              {notification.is_active ? "Attiva" : "Inattiva"}
                            </BrutalBadge>
                            {notification.is_evergreen && (
                              <BrutalBadge variant="success">
                                Evergreen
                              </BrutalBadge>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Audience badges */}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {getAudienceBadges(notification).map((target) => (
                            <BrutalBadge
                              key={target}
                              variant="secondary"
                            >
                              {getAudienceLabel(target)}
                            </BrutalBadge>
                          ))}
                        </div>
                      </td>

                      {/* CTA */}
                      <td className="px-4 py-3">
                        {notification.cta_url ? (
                          <a
                            href={notification.cta_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                          >
                            <Link2 className="h-3 w-3" />
                            Link
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>

                      {/* Send Email */}
                      <td className="px-4 py-3 text-center">
                        {notification.send_email ? (
                          <Mail className="h-4 w-4 text-green-600 mx-auto" />
                        ) : (
                          <MailX className="h-4 w-4 text-muted-foreground mx-auto" />
                        )}
                      </td>

                      {/* Date */}
                      <td className="px-4 py-3">
                        {notification.created_at ? (
                          <span className="text-sm text-muted-foreground">
                            {format(new Date(notification.created_at), "dd MMM yyyy", {
                              locale: localeMap[i18n.language as keyof typeof localeMap] || it,
                            })}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <TooltipProvider>
                          <div className="flex items-center justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <BrutalButton
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenEdit(notification)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </BrutalButton>
                              </TooltipTrigger>
                              <TooltipContent>Modifica</TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <BrutalButton
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    toggleActiveMutation.mutate({
                                      id: notification.id,
                                      isActive: !notification.is_active,
                                    })
                                  }
                                >
                                  <Power
                                    className={`h-4 w-4 ${
                                      notification.is_active
                                        ? "text-green-600"
                                        : "text-muted-foreground"
                                    }`}
                                  />
                                </BrutalButton>
                              </TooltipTrigger>
                              <TooltipContent>
                                {notification.is_active ? "Disattiva" : "Attiva"}
                              </TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <BrutalButton
                                  variant="danger"
                                  size="sm"
                                  onClick={() => setDeleteNotificationId(notification.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </BrutalButton>
                              </TooltipTrigger>
                              <TooltipContent>Elimina</TooltipContent>
                            </Tooltip>
                          </div>
                        </TooltipProvider>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </BrutalCard>
        )}

        {/* Create/Edit Modal */}
        <NotificationFormModal
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          notification={editingNotification}
        />

        {/* Delete Confirmation */}
        <AlertDialog
          open={!!deleteNotificationId}
          onOpenChange={() => setDeleteNotificationId(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("admin.notifications.deleteConfirmTitle", "Elimina notifica")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(
                  "admin.notifications.deleteConfirmDesc",
                  "Sei sicuro di voler eliminare questa notifica? Questa azione non può essere annullata."
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteNotificationId && deleteMutation.mutate(deleteNotificationId)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("common.remove")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
